import { NcFileBatchEventType } from '@v2/pages/common/enums';
import { TimeCategory } from '@interfaces/base';
import { ClampingDetails } from '@v2/context/Times/page/interfaces';
import { EventTimelineData } from '@v2/pages/common/components/NCFileBatchEvents/types';
import { Timestamps } from '@v2/pages/common/components/NCFileBatchEvents/components/TimeTiles/types';
import { ACTIVE_TIME_COEF_IN_CASE_CYCLE_TIME_ZERO } from '@v2/pages/common/components/NCFileBatchEvents/components/TimeTiles/constants';

interface BuildUpdatedStatisticFieldsParams {
  eventType: NcFileBatchEventType | undefined;
  timeCategory: TimeCategory | undefined;
  timestamps: Timestamps;
  eventTimelineData: EventTimelineData | undefined;
}

const buildUpdatedStatisticFields = ({
  eventType,
  timeCategory,
  timestamps,
  eventTimelineData,
}: BuildUpdatedStatisticFieldsParams): Partial<ClampingDetails> | null => {
  if (!eventType || !timeCategory) return null;

  const { updatedTime, replacedTime } = timestamps;
  const isCycleEvent = eventType === NcFileBatchEventType.CYCLE;

  // Handle CYCLE event type - updates both active and change times
  if (isCycleEvent) {
    const replacedTimeWasZero = replacedTime === 0;
    const coef = replacedTimeWasZero ? ACTIVE_TIME_COEF_IN_CASE_CYCLE_TIME_ZERO : updatedTime / replacedTime;

    const cycleTimesConfig: Partial<Record<TimeCategory, Record<NcFileBatchEventType.ACTIVE | NcFileBatchEventType.CHANGE, number>>> = {
      [TimeCategory.IDEAL]: {
        [NcFileBatchEventType.ACTIVE]: eventTimelineData?.idealActiveTimeInSec || 0,
        [NcFileBatchEventType.CHANGE]: eventTimelineData?.idealChangeTimeInSec || 0,
      },
      [TimeCategory.SCHEDULED]: {
        [NcFileBatchEventType.ACTIVE]: eventTimelineData?.scheduledActiveTimeInSec || 0,
        [NcFileBatchEventType.CHANGE]: eventTimelineData?.scheduledChangeTimeInSec || 0,
      },
      [TimeCategory.PAID]: {
        [NcFileBatchEventType.ACTIVE]: eventTimelineData?.paidActiveTimeInSec || 0,
        [NcFileBatchEventType.CHANGE]: eventTimelineData?.paidChangeTimeInSec || 0,
      },
    };

    const replacedCycleTimes = cycleTimesConfig[timeCategory];
    if (!replacedCycleTimes) return null;

    const calcUpdatedTime = (event: NcFileBatchEventType.ACTIVE | NcFileBatchEventType.CHANGE, coefForCycleZeroTime: number = 1) => {
      return !replacedTimeWasZero ? replacedCycleTimes[event] * coef : updatedTime * coefForCycleZeroTime;
    };

    const roundedUpdatedActiveCycleTime = Math.round(calcUpdatedTime(NcFileBatchEventType.ACTIVE, ACTIVE_TIME_COEF_IN_CASE_CYCLE_TIME_ZERO));
    const roundedUpdatedChangeCycleTime = Math.round(calcUpdatedTime(NcFileBatchEventType.CHANGE, 1 - ACTIVE_TIME_COEF_IN_CASE_CYCLE_TIME_ZERO));

    if (timeCategory === TimeCategory.IDEAL) {
      return {
        runTimePerPcsSecond: roundedUpdatedActiveCycleTime,
        useManualIdealRunTime: true,
        changeTimePerPcsSecond: roundedUpdatedChangeCycleTime,
        useManualIdealChangeTime: true,
      };
    }

    if (timeCategory === TimeCategory.SCHEDULED) {
      return {
        scheduledRunTimeInSeconds: roundedUpdatedActiveCycleTime,
        useManualScheduledRunTime: true,
        scheduledChangeTimeInSeconds: roundedUpdatedChangeCycleTime,
        useManualScheduledChangeTime: true,
      };
    }

    if (timeCategory === TimeCategory.PAID) {
      return {
        paidRunTimePerPcsSecond: roundedUpdatedActiveCycleTime,
        paidChangeTimePerPcsSecond: roundedUpdatedChangeCycleTime,
      };
    }

    return null;
  }

  // Handle non-CYCLE event types
  const updatedStatisticTimesConfig: Partial<Record<NcFileBatchEventType, Partial<Record<TimeCategory, Partial<ClampingDetails>>>>> = {
    [NcFileBatchEventType.ACTIVE]: {
      [TimeCategory.IDEAL]: {
        runTimePerPcsSecond: updatedTime,
        useManualIdealRunTime: true,
      },
      [TimeCategory.SCHEDULED]: {
        scheduledRunTimeInSeconds: updatedTime,
        useManualScheduledRunTime: true,
      },
      [TimeCategory.PAID]: {
        paidRunTimePerPcsSecond: updatedTime,
      },
    },
    [NcFileBatchEventType.CHANGE]: {
      [TimeCategory.IDEAL]: {
        changeTimePerPcsSecond: updatedTime,
        useManualIdealChangeTime: true,
      },
      [TimeCategory.SCHEDULED]: {
        scheduledChangeTimeInSeconds: updatedTime,
        useManualScheduledChangeTime: true,
      },
      [TimeCategory.PAID]: {
        paidChangeTimePerPcsSecond: updatedTime,
      },
    },
    [NcFileBatchEventType.SETUP]: {
      [TimeCategory.IDEAL]: {
        setupTimeSecond: updatedTime,
        useManualIdealSetupTime: true,
      },
      [TimeCategory.SCHEDULED]: {
        scheduledSetupTimeInSeconds: updatedTime,
        useManualScheduledSetupTime: true,
      },
      [TimeCategory.PAID]: {
        paidSetupTimeSecond: updatedTime,
      },
    },
  };

  return updatedStatisticTimesConfig[eventType]?.[timeCategory] || null;
};

export { buildUpdatedStatisticFields };
